/* LG EXPRESS — Hero + section motion (editorial)
   - Word-by-word reveal on scroll
   - Fade-up reveal on [data-lg-fade]
   - Scroll parallax on [data-lg-parallax-el] within [data-lg-parallax-root]
   - Hero smooth fade on scroll
   - Magnetic buttons
   - Blur-up images
   No canvas particles, no gimmicks.
*/
(function () {
  'use strict';

  const reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ===== FADE-UP on scroll ===== */
  function initFades() {
    if (!('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-lg-fade]');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const idx = parseInt(e.target.getAttribute('data-lg-fade-idx') || '0', 10);
        e.target.style.transitionDelay = `${Math.min(idx, 20) * 65}ms`;
        e.target.setAttribute('data-lg-in', 'true');
        io.unobserve(e.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ===== BLUR-UP images ===== */
  function initBlurUp() {
    if (!('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-lg-blur-up]');
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        // Wait for the image to finish decoding to avoid flash of unsharp
        if (e.target.tagName === 'IMG' && !e.target.complete) {
          e.target.addEventListener('load', () => e.target.setAttribute('data-lg-in', 'true'), { once: true });
        } else {
          e.target.setAttribute('data-lg-in', 'true');
        }
        io.unobserve(e.target);
      });
    }, { threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }

  /* ===== SCROLL PARALLAX (throttled rAF) ===== */
  function initParallax() {
    if (reduceMotion) return;
    const roots = document.querySelectorAll('[data-lg-parallax-root]');
    if (!roots.length) return;
    const items = [];
    roots.forEach((root) => {
      root.querySelectorAll('[data-lg-parallax-el]').forEach((el) => {
        const speed = parseFloat(el.dataset.lgParallaxSpeed || '0.2');
        items.push({ root, el, speed });
      });
    });
    if (!items.length) return;

    let ticking = false;
    function update() {
      const vh = window.innerHeight;
      items.forEach(({ root, el, speed }) => {
        const rect = root.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const centre = rect.top + rect.height / 2;
        const delta = centre - vh / 2;
        const y = -delta * speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ===== HERO smooth fade on scroll ===== */
  function initHeroScrollFade() {
    if (reduceMotion) return;
    const hero = document.querySelector('[data-lg-hero]');
    if (!hero) return;
    const inner = hero.querySelector('.lg-hero__inner');
    if (!inner) return;

    let ticking = false;
    function update() {
      const y = window.scrollY;
      const h = hero.offsetHeight;
      const progress = Math.min(1, Math.max(0, y / (h * 0.85)));
      inner.style.opacity = String(1 - progress * 0.9);
      inner.style.transform = `translate3d(0, ${(progress * 34).toFixed(1)}px, 0)`;
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ===== MAGNETIC BUTTONS ===== */
  function initMagnetic() {
    if (reduceMotion) return;
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('[data-lg-magnetic]').forEach((el) => {
      if (el.dataset.lgMagInit === '1') return;
      el.dataset.lgMagInit = '1';
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const dx = (e.clientX - r.left - r.width / 2) * 0.18;
        const dy = (e.clientY - r.top - r.height / 2) * 0.18;
        el.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px)`;
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
      });
    });
  }

  /* ===== STICKY HEADER SHADOW (add class on scroll) ===== */
  function initStickyHeader() {
    const header = document.getElementById('header-group');
    if (!header) return;
    function update() {
      if (window.scrollY > 40) header.setAttribute('data-lg-scrolled', 'true');
      else header.removeAttribute('data-lg-scrolled');
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  function boot() {
    initFades();
    initBlurUp();
    initParallax();
    initHeroScrollFade();
    initMagnetic();
    initStickyHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
